import { useState, ImgHTMLAttributes } from "react";
import { 
  generateImageSrcSet, 
  generateImageSizes, 
  getBlurPlaceholder,
  isPrintFile,
  getOptimizedUrl
} from "@/lib/imageOptimization";

interface OptimizedImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet' | 'sizes'> {
  src: string;
  alt: string;
  /**
   * Sizes for srcset generation
   * @default [400, 800, 1200]
   */
  responsiveSizes?: number[];
  /**
   * Quality setting (1-100)
   * @default 75
   */
  quality?: number;
  /**
   * Max width for sizes attribute
   * @default '100vw'
   */
  maxWidth?: string;
  /**
   * Enable blur placeholder for progressive loading
   * @default false
   */
  enableBlur?: boolean;
  /**
   * CRITICAL: Set to true if this is a print file that should NOT be optimized
   * Print files need full quality for Printful
   * @default false
   */
  isPrint?: boolean;
}

/**
 * OptimizedImage Component
 * 
 * Automatically optimizes images with:
 * - Responsive srcset for different screen sizes
 * - Progressive loading with blur placeholder
 * - Lazy loading
 * - Proper alt text for accessibility
 * 
 * CRITICAL: This component will NEVER optimize print files marked with isPrint=true
 * or files that match print file patterns (printful-, print-file-, etc.)
 */
export function OptimizedImage({
  src,
  alt,
  responsiveSizes = [400, 800, 1200],
  quality = 80,
  maxWidth = '100vw',
  enableBlur = false,
  isPrint = false,
  className = "",
  loading = "lazy",
  ...props
}: OptimizedImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Safety check for undefined/null/empty src
  if (!src || typeof src !== 'string') {
    return (
      <div 
        className={`flex items-center justify-center bg-muted ${className}`}
        {...props}
      >
        <span className="text-muted-foreground text-sm">Bilden kunde inte laddas</span>
      </div>
    );
  }

  // CRITICAL: Check if this is a print file that should NOT be optimized
  const shouldOptimize = !isPrint && !isPrintFile(src);

  // For print files, use the original URL without any optimization
  if (!shouldOptimize) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        loading={loading}
        onError={() => setHasError(true)}
        {...props}
      />
    );
  }

  // For display images, apply optimization
  const srcSet = generateImageSrcSet({ url: src, sizes: responsiveSizes, quality });
  const sizes = generateImageSizes(maxWidth);
  const blurUrl = enableBlur ? getBlurPlaceholder(src) : undefined;
  const mainUrl = getOptimizedUrl(src, responsiveSizes[1] || 800, quality);
  
  // Preload the main image for faster perceived loading
  if (loading === "eager" && mainUrl) {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = mainUrl;
    if (!document.querySelector(`link[href="${mainUrl}"]`)) {
      document.head.appendChild(link);
    }
  }

  if (hasError) {
    return (
      <div 
        className={`flex items-center justify-center bg-muted ${className}`}
        {...props}
      >
        <span className="text-muted-foreground text-sm">Bilden kunde inte laddas</span>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Blur placeholder */}
      {enableBlur && blurUrl && !isLoaded && (
        <img
          src={blurUrl}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover blur-xl scale-110"
        />
      )}
      
      {/* Main image */}
      <img
        src={mainUrl}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        loading={loading}
        onLoad={() => setIsLoaded(true)}
        onError={() => setHasError(true)}
        className={`
          w-full h-full object-cover transition-opacity duration-150
          ${isLoaded ? 'opacity-100' : 'opacity-0'}
        `}
        {...props}
      />
    </div>
  );
}
